import { useRef, useState } from 'react'
import {
  Button,
  Heading,
  InlineAlert,
  Menu,
  Table,
} from '@design-systems/canvas-ui'
import Icon, { PlusIcon } from '@design-systems/react-icons'
import { getApiErrorMessage } from '../../../shared/api/apiClient'
import ConfigurationModal from './ConfigurationModal'

const getComparableFieldValue = (field, values) => {
  if (field.type === 'switch') {
    return Boolean(values[field.id])
  }

  return `${values[field.id] ?? ''}`
}

const hasChangedFields = (fields, initialValues, values) =>
  fields.some(
    (field) =>
      getComparableFieldValue(field, initialValues) !==
      getComparableFieldValue(field, values),
  )

const getColumnValue = (item, column) => {
  if (column.variant === 'status') {
    return item[column.id] === false
      ? (column.falseLabel ?? 'Inactive')
      : (column.trueLabel ?? 'Active')
  }
  const value = item[column.id]
  return value === undefined || value === null ? '' : `${value}`
}

const getColumnContentClassName = (column, type = 'cell') => {
  const classNames = [
    type === 'header'
      ? 'configuration-list__header-content'
      : 'configuration-list__cell-content',
  ]

  if (column.align === 'center') {
    classNames.push(
      type === 'header'
        ? 'configuration-list__header-content--center'
        : 'configuration-list__cell-content--center',
    )
  }

  if (column.variant === 'actions') {
    classNames.push(
      type === 'header'
        ? 'configuration-list__header-content--actions'
        : 'configuration-list__cell-content--actions',
    )
  }

  return classNames.join(' ')
}

const getColumnContentStyle = (column) => ({
  ...(column.width ? { width: column.width } : {}),
  ...(column.maxWidth ? { maxWidth: column.maxWidth } : {}),
})

const normalizeActionItems = (actionConfig) =>
  (actionConfig?.actions ?? []).map((action) =>
    typeof action === 'string' ? { label: action } : action,
  )

const normalizeFieldValues = (fields, values) =>
  Object.entries(values).reduce((normalizedValues, [fieldId, value]) => {
    const field = fields.find(({ id }) => id === fieldId)

    normalizedValues[fieldId] = field?.normalize
      ? field.normalize(value)
      : value

    return normalizedValues
  }, {})

const ConfigurationSection = ({
  error,
  isLoading,
  items = [],
  onCreate,
  onUpdate,
  section,
}) => {
  const {
    actionConfig,
    columns,
    createButtonLabel,
    createSubmitLabel,
    derivedFields = [],
    emptyForm,
    emptyMessage,
    entityName,
    fields,
    getDisplayName,
    listLabel,
    loadErrorHeading,
    loadingMessage,
    savedHeading,
    title,
    toFormValues,
  } = section
  const actionItems = normalizeActionItems(actionConfig)
  const renderedColumns = columns.some((column) => column.id === 'actions')
    ? columns
    : [
        ...columns,
        ...(actionItems.length > 0
          ? [{ id: 'actions', label: 'Actions', variant: 'actions' }]
          : []),
      ]
  const [form, setForm] = useState(emptyForm)
  const [selectedItem, setSelectedItem] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [requestError, setRequestError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  // Derived fields stop following their source once they hold a value the
  // person typed themselves, or one that already came back from the API.
  const pinnedFields = useRef(new Set())
  const initialForm = useRef(emptyForm)
  const isEditing = Boolean(selectedItem)
  const hasPendingChanges =
    !isEditing || hasChangedFields(fields, initialForm.current, form)

  const updateForm = (values) => {
    const normalizedValues = normalizeFieldValues(fields, values)

    derivedFields.forEach(({ to }) => {
      if (to in normalizedValues) pinnedFields.current.add(to)
    })

    setForm((current) => {
      const next = { ...current, ...normalizedValues }
      derivedFields.forEach(({ from, to, transform }) => {
        if (from in normalizedValues && !pinnedFields.current.has(to)) {
          next[to] = transform(next[from])
        }
      })
      return next
    })
  }

  const closeModal = () => {
    pinnedFields.current = new Set()
    initialForm.current = emptyForm
    setForm(emptyForm)
    setSelectedItem(null)
    setIsModalOpen(false)
    setRequestError('')
  }

  const openCreateModal = () => {
    setSuccessMessage('')
    pinnedFields.current = new Set()
    initialForm.current = emptyForm
    setForm(emptyForm)
    setSelectedItem(null)
    setRequestError('')
    setIsModalOpen(true)
  }

  const openEditModal = (item) => {
    const values = toFormValues(item)
    setSuccessMessage('')
    pinnedFields.current = new Set(
      derivedFields.filter(({ to }) => values[to]).map(({ to }) => to),
    )
    initialForm.current = values
    setForm(values)
    setSelectedItem(item)
    setRequestError('')
    setIsModalOpen(true)
  }

  const renderColumnContent = (item, column) => {
    const contentClassName = getColumnContentClassName(column)
    const contentStyle = getColumnContentStyle(column)

    if (column.variant === 'link') {
      return (
        <div className={contentClassName} style={contentStyle}>
          <a
            href={`#${item.id}`}
            onClick={(event) => {
              event.preventDefault()
              openEditModal(item)
            }}
          >
            {getColumnValue(item, column)}
          </a>
        </div>
      )
    }

    if (column.variant === 'status') {
      return (
        <div className={contentClassName} style={contentStyle}>
          <span
            className={`configuration-list__status ${
              item[column.id] === false
                ? 'configuration-list__status--inactive'
                : ''
            }`}
          >
            {getColumnValue(item, column)}
          </span>
        </div>
      )
    }

    if (column.variant === 'actions' && actionItems.length > 0) {
      return (
        <div className={contentClassName} style={contentStyle}>
          <Menu location="bottom-right">
            <Menu.Toggle
              aria-label={`Open actions for ${
                getDisplayName(item) ?? item.name ?? item.label ?? 'item'
              }`}
              size="lg"
              variant="ghost-neutral"
            >
              <span aria-hidden="true">⋮</span>
            </Menu.Toggle>
            {actionItems.map((action) => (
              <Menu.Item
                key={action.label}
                as="button"
                className="configuration-list__action-item"
                onClick={() => action.onClick?.(item)}
                type="button"
              >
                {action.label}
              </Menu.Item>
            ))}
          </Menu>
        </div>
      )
    }

    return (
      <div className={contentClassName} style={contentStyle}>
        {getColumnValue(item, column)}
      </div>
    )
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (isEditing && !hasPendingChanges) {
      return
    }

    setRequestError('')
    setSuccessMessage('')
    setIsSaving(true)

    const displayName = `${getDisplayName(form) ?? ''}`.trim()

    try {
      if (isEditing) {
        await onUpdate(selectedItem.id, form)
        setSuccessMessage(`${displayName} was updated successfully.`)
      } else {
        await onCreate(form)
        setSuccessMessage(`${displayName} was created successfully.`)
      }
      closeModal()
    } catch (saveError) {
      setRequestError(
        getApiErrorMessage(
          saveError,
          `We could not ${isEditing ? 'update' : 'create'} the ${entityName}. Please try again.`,
        ),
      )
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="settings-page__content">
      <div className="configuration-list__heading">
        <Heading as="h2" size="md">
          {isLoading ? title : `${items.length} ${title}`}
        </Heading>
        <Button onClick={openCreateModal} variant="primary">
          <Icon size="sm" svg={PlusIcon} />
          {createButtonLabel}
        </Button>
      </div>

      {successMessage && (
        <InlineAlert heading={savedHeading} variant="success">
          {successMessage}
        </InlineAlert>
      )}

      {isLoading && <p role="status">{loadingMessage}</p>}
      {error && !isLoading && (
        <InlineAlert heading={loadErrorHeading} variant="error">
          Refresh the page to try again.
        </InlineAlert>
      )}
      {!isLoading && !error && items.length === 0 && (
        <p className="configuration-list__empty">{emptyMessage}</p>
      )}
      {!isLoading && !error && items.length > 0 && (
        <div
          className="configuration-list"
          role="region"
          aria-label={listLabel}
        >
          <Table
            cellPadding="normal"
            className="configuration-list__table"
            data={{
              columnDefs: renderedColumns.map((column) => ({
                id: column.id,
                header: (
                  <span
                    className={getColumnContentClassName(column, 'header')}
                    style={getColumnContentStyle(column)}
                  >
                    {column.label}
                  </span>
                ),
                accessorFn: (item) => getColumnValue(item, column),
                cell: ({ row }) => renderColumnContent(row.original, column),
                width: column.width,
              })),
              rowData: items,
            }}
            isCornerRounded
            name={listLabel}
          />
        </div>
      )}

      <ConfigurationModal
        error={requestError}
        fields={fields}
        isEditing={isEditing}
        isOpen={isModalOpen}
        isSaving={isSaving}
        isSubmitDisabled={!hasPendingChanges}
        onChange={updateForm}
        onClose={closeModal}
        onSubmit={handleSubmit}
        submitLabel={isEditing ? 'Save changes' : createSubmitLabel}
        title={`${isEditing ? 'Edit' : 'Create'} ${entityName}`}
        values={form}
      />
    </div>
  )
}

export default ConfigurationSection
